import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { SnapshotDataMap } from "csi/state/3D/snapshotsStore";
import { useStore } from "csi/state/hooks";
import rootStore from "csi/state/rootStore";
import { Box, Checkbox, CssGrid, FlexRow, IconButton } from "designSystem/index";
import { grey } from "designSystem/styles/color";
import { observer } from "mobx-react-lite";
import React, { useState, useEffect } from "react";
import { SnapshotImage } from "./SnapshotImage";
import { SearchPlaceholder } from "designSystem/index";

interface ListItemProps {
	/* list of snapshots */
    listData: SnapshotDataMap[];

	/* title if not using localization (i.e. variant names)*/
	label?: string;

	/* localized title  */
	res?: string;
}

const SnapshotList = observer(() => {
	const { generatedImages, turntableImages, userImages, groupByGenerationType, getVariantsFromSnapshots, showNoResultsFound } = rootStore.threeDStore.snapshotsStore;
	const { modelVariants } = rootStore.threeDStore.canvasStore;

	return (
		<Box
			flexGrow={1}
			p={3}
			sx={{
				overflow: "auto",
				paddingTop: 0,
			}}
		>
			{
				groupByGenerationType 
					? (
						<>
							<DataList listData={generatedImages} res='3DNumOfImages' />
							<DataList listData={turntableImages} res='3DNumOfTurntableImages' />
							<DataList listData={userImages} res='3DNumOfSnapshots' />
						</>	
					)
					: (
						modelVariants?.map((variant, index) => (
							<DataList
								key={index}
								label={variant}
								listData={getVariantsFromSnapshots(variant)}
							/>
						))
					)
			}
			{ showNoResultsFound && (
				<SearchPlaceholder
					subtitleProps={{
						res: "3DSnapshotsFilterNoResults",
					}}
					sx={{
						height: "100%",
						justifyContent: "center",
					}}
					titleProps={{
						res: "SEARCH_NO_RESULTS",
						sx: {
							textTransform: "capitalize",
						},
					}}
				/>
			)}
		</Box>
	);
});

const DataList = observer((props: ListItemProps) => {
	const { listData, label, res } = props;
	const { selectedCameraPositionsFilter } = rootStore.threeDStore.snapshotsStore;
	const [isVisible, setIsVisible] = useState(true);

	useEffect(() => {
		if (selectedCameraPositionsFilter.length > 0) {
			setIsVisible(true);
		}
	}, [selectedCameraPositionsFilter]);

	if (listData.length === 0) {
		return null;
	}

	return (
		<>
			<Header
				isVisible={isVisible}
				label={label}
				listData={listData}
				res={res}
				toggleList={() => setIsVisible(!isVisible)}
			/>
			{
				isVisible && (
					<CssGrid 
						columnsByWidth={[
							[2, 1],
							[3, 438],
						]}
						spacing={3}
					>
						{
							listData.map((item, index) => (
								<SnapshotImage
									data={item}
									index={index}
									key={index}
								/>
							))
						}
					</CssGrid>
				)
			}
		</>
	);
});

const Header = observer((props: {label: string; res: string; listData: SnapshotDataMap[]; toggleList: () => void; isVisible: boolean }) => {
	const { label, listData, res, toggleList, isVisible } = props;
	const { threeDStore } = useStore();
	const { setSelectedSnapshots } = threeDStore.snapshotsStore;

	// set checked to true if all snapshots are selected
	const checked = listData.every((snapshot) => snapshot.isSelected);

	return (
		<FlexRow
			justifyContent="space-between"
			paddingBottom={2}
			paddingTop={2}
		>
			<Checkbox
				checked={checked}
				labelProps={{
					label: `${label} (${listData.length})`,
					res: res,
					resParams: [listData.length],
					componentsProps: {
						typography: {
							textTransform: "uppercase",
							fontWeight: "700",
							color: `${grey["A100"]} !important`,
						},
					},
				}}
				onChange={(e, val)=> setSelectedSnapshots(val, listData)}
			/>
			<IconButton onClick={toggleList}>
				<KeyboardArrowUpIcon
					sx={{
						transform: `rotate(${isVisible ? 0 : 180}deg)`,
						transition: "transform 0.2s ease-in-out",
				
					}}
				/>
			</IconButton>
		</FlexRow>
	);
});

export default SnapshotList;